'use client';

import Link from 'next/link';

import { useQuery } from '@tanstack/react-query';

import { PersonalAccountDropdown } from '@kit/accounts/personal-account-dropdown';
import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { JWTUserData } from '@kit/supabase/types';

import featuresFlagConfig from '~/config/feature-flags.config';
import pathsConfig from '~/config/paths.config';
import { UserAccountDropdown } from '~/(tenant)/_components/user-account-dropdown';

const paths = {
  home: pathsConfig.app.home,
  bookCourt: '/book',
};

const features = {
  enableThemeToggle: featuresFlagConfig.enableThemeToggle,
};

interface PadelDistrictHeaderAccountSectionProps {
  onNavigate?: () => void;
}

/**
 * Session-aware account affordance for the shared PadelDistrict shell header.
 * Mirrors the BPadel pattern: signed-out users get Book + Sign In; signed-in
 * users get Book + their account dropdown (admin link for staff). Brand styling
 * uses the Bright Blue CTA (#015EFF) per the landing page guidelines.
 */
export function PadelDistrictHeaderAccountSection({
  onNavigate,
}: PadelDistrictHeaderAccountSectionProps) {
  const session = useSession();
  const userRole = useUserRole(session.data?.user?.id);
  const teamAccount = useTeamAccount(session.data?.user?.id);
  const signOut = useSignOut({
    onSuccess: () => window.location.reload(),
  });

  if (session.data) {
    const role = userRole.data || 'guest';

    if (role === 'owner' || role === 'member') {
      return (
        <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
          <Link
            href={paths.bookCourt}
            onClick={onNavigate}
            className="btn-green"
            style={{ padding: '0.6rem 1.4rem', fontSize: '0.72rem' }}
          >
            Book Now
          </Link>

          <Link
            href={
              teamAccount.data
                ? `/home/${teamAccount.data.slug}/dashboard`
                : '/home'
            }
            onClick={onNavigate}
            className="hidden md:inline-flex items-center justify-center rounded-full border border-white/30 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-white/80 transition-colors hover:bg-white/10 hover:text-white"
            style={{ fontFamily: "'DM Sans',sans-serif" }}
          >
            Admin
          </Link>

          <PersonalAccountDropdown
            showProfileName={false}
            paths={paths}
            features={features}
            user={session.data.user as unknown as JWTUserData}
            signOutRequested={() => signOut.mutateAsync()}
            className="z-50"
          />
        </div>
      );
    }

    return (
      <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
        <Link
          href={paths.bookCourt}
          onClick={onNavigate}
          className="btn-green"
          style={{ padding: '0.6rem 1.4rem', fontSize: '0.72rem' }}
        >
          Book Now
        </Link>

        <UserAccountDropdown
          showProfileName={false}
          features={features}
          user={session.data.user as unknown as JWTUserData}
          signOutRequested={() => signOut.mutateAsync()}
          className="z-50"
        />
      </div>
    );
  }

  return <AuthButtons onNavigate={onNavigate} />;
}

function AuthButtons({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <div className="animate-in fade-in flex items-center gap-x-3 duration-500">
      {/* Hard navigation avoids hydration mismatch across route groups. */}
      <a
        href={pathsConfig.auth.signIn}
        onClick={onNavigate}
        className="nav-link"
        style={{ color: 'rgba(255,255,255,0.45)' }}
      >
        Sign In
      </a>

      <Link
        href={paths.bookCourt}
        onClick={onNavigate}
        className="btn-green"
        style={{ padding: '0.6rem 1.4rem', fontSize: '0.72rem' }}
      >
        Book Now
      </Link>
    </div>
  );
}

function useSession() {
  const client = useSupabase();

  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
  });
}

function useUserRole(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['user-role', userId],
    queryFn: async () => {
      if (!userId) return 'guest';

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select('account_role')
          .eq('user_id', userId);

        if (memberships && memberships.length > 0) {
          if (memberships.some((m) => m.account_role === 'owner')) {
            return 'owner';
          }
          return 'member';
        }

        return 'user';
      } catch {
        return 'guest';
      }
    },
    enabled: !!userId,
  });
}

function useTeamAccount(userId?: string) {
  const client = useSupabase();

  return useQuery({
    queryKey: ['team-account', userId],
    queryFn: async () => {
      if (!userId) return null;

      try {
        const { data: memberships } = await client
          .from('accounts_memberships')
          .select(
            `
            account_id,
            accounts!accounts_memberships_account_id_fkey!inner (
              id,
              slug,
              name,
              is_personal_account
            )
          `,
          )
          .eq('user_id', userId)
          .eq('accounts.is_personal_account', false);

        if (memberships && memberships.length > 0) {
          return memberships[0]?.accounts;
        }

        return null;
      } catch {
        return null;
      }
    },
    enabled: !!userId,
  });
}
